'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { type CSSProperties, useRef, useState } from 'react';
import { useThemeContext } from '../../../utils/providers/theme-context';

type CSSWithVars = CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

type ColorToken =
  | 'activeText'
  | 'normalText'
  | 'secondLayer'
  | 'border'
  | 'borderPrimary'
  | 'inputBorder'
  | 'icon'
  | 'inactiveText'
  | 'dropdownText'
  | 'baseLayer'
  | 'firstLayer'
  | 'thirdLayer'
  | 'datagridBg';

type ComboboxBaseProps<T> = Omit<
  Headless.ComboboxProps<T, false>,
  'as' | 'multiple' | 'children' | 'value' | 'defaultValue'
>;

export function Combobox<T>({
  options,
  displayValue,
  filter,
  anchor = 'bottom',
  className,
  placeholder,
  autoFocus,
  required,
  'aria-label': ariaLabel,
  children,
  onQueryChange,
  serverFilter = false,
  value = null,
  defaultValue = null,
  ...rest
}: {
  options: T[];
  displayValue: (value: T | null) => string; // ✅ must return string
  filter?: (value: T, query: string) => boolean;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  children: (value: NonNullable<T>) => React.ReactElement;
  onQueryChange?: (q: string) => void;
  serverFilter?: boolean;
  anchor?: 'top' | 'bottom';
  value?: T | null;
  defaultValue?: T | null;
  required?: boolean;
} & ComboboxBaseProps<T>) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const themeContext = useThemeContext();
  const theme = themeContext?.theme ?? 'light';
  const brandColor = themeContext?.brandColor ?? null;
  const getColour = themeContext?.getColour;

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const safeColour = (token: ColorToken): string =>
    getColour?.(token) ?? 'inherit';

  const active = brandColor ?? safeColour('activeText');
  const text = safeColour('normalText');
  const surface = safeColour('secondLayer');
  const borderBase = safeColour('border');
  const borderHover = safeColour('border');
  const placeholderColor = safeColour('inactiveText');
  const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');
  const ring = active;

  const controlBg = theme === 'dark' ? 'transparent' : surface;
  const menuBg = surface;

  const styleVars: CSSWithVars = {
    '--in-bg': surface,
    '--in-fg': text,
    '--in-ph': placeholderColor,
    '--in-border': borderBase,
    '--in-border-hover': borderHover,
    '--in-ring': ring,
    '--in-disabled-bg': disabledBg,
    '--in-ctrl-bg': controlBg,
    '--in-menu-bg': menuBg,
    '--in-shadow': '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
    '--in-focus-shadow':
      '0 2px 6px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)'
  };

  const filteredOptions = serverFilter
    ? options
    : query === ''
      ? options
      : options.filter(option =>
          filter
            ? filter(option, query)
            : displayValue(option).toLowerCase().includes(query.toLowerCase())
        );

  return (
    <Headless.Combobox
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(rest as any)}
      multiple={false}
      nullable // ✅ allows value={null}
      immediate // opens the option list on focus, so clicking the input text is enough
      value={value}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      defaultValue={defaultValue as any}
      virtual={{ options: filteredOptions }}
      onClose={() => setQuery('')}
    >
      <span
        data-slot="control"
        className="relative block w-full cursor-pointer"
        style={styleVars}
      >
        <Headless.ComboboxInput
          ref={inputRef}
          autoFocus={autoFocus}
          required={required}
          data-slot="control"
          aria-label={ariaLabel}
          // Open on any input click. Headless's ComboboxButton opens
          // on ``pointerdown``, so ``.click()`` won't do it — we
          // dispatch a real pointerdown to trigger the arrow opener.
          onClick={() =>
            buttonRef.current?.dispatchEvent(
              new PointerEvent('pointerdown', { bubbles: true, button: 0 })
            )
          }
          displayValue={(option: T) => displayValue(option)}
          onChange={event => {
            const q = event.target.value;
            setQuery(q);
            onQueryChange?.(q);
          }}
          placeholder={placeholder}
          className={clsx(
            'relative block w-full appearance-none rounded-2xl px-3 border transition-colors',
            // Kill the forms-plugin ring; match the global :focus-visible
            // backstop selector's specificity to also drop its blue outline
            // so the border colour is the single focus indicator.
            'focus:ring-0!',
            '[&:focus-visible:not([contenteditable="true"]):not([contenteditable=""])]:outline-none!',
            'bg-[var(--in-ctrl-bg)] text-[var(--in-fg)] placeholder-[var(--in-ph)]',
            'border-[var(--in-border)] hover:border-[var(--in-border-hover)]',
            'focus:outline-none focus:border-[var(--in-ring)]',
            'shadow-[var(--in-shadow)] focus:shadow-[var(--in-focus-shadow)]',
            'dark:scheme-dark',
            className
          )}
          style={{ paddingRight: 'calc(2.5rem)' }}
        />

        <Headless.ComboboxButton
          ref={buttonRef}
          aria-label={ariaLabel}
          className="group absolute inset-y-0 right-0 flex items-center px-2"
        >
          <svg
            className="size-5 sm:size-4"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
            style={{ stroke: 'var(--in-ph)' }}
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Headless.ComboboxButton>
      </span>

      <Headless.ComboboxOptions
        transition
        anchor={anchor}
        className={clsx(
          '[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(4)] sm:data-[anchor~=start]:[--anchor-offset:-4px]',
          'isolate min-w-[calc(var(--input-width)+8px)] scroll-py-1 rounded-2xl p-1 select-none empty:invisible',
          'outline outline-transparent focus:outline-hidden',
          'overflow-y-scroll overscroll-contain',
          'shadow-pop ring-1',
          'transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none',
          'bg-[var(--in-menu-bg)] text-[var(--in-fg)] ring-[var(--in-border)]'
        )}
        style={styleVars}
      >
        {({ option }) => children(option)}
      </Headless.ComboboxOptions>
    </Headless.Combobox>
  );
}

export function ComboboxOption<T>({
  children,
  className,
  ...props
}: { className?: string; children?: React.ReactNode } & Omit<
  Headless.ComboboxOptionProps<'div', T>,
  'as' | 'className'
>) {
  const sharedClasses = clsx(
    'flex min-w-0 items-center',
    '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-4',
    '[&_*[data-slot=icon]]:text-[var(--in-ph)] group-data-focus/option:[&_*[data-slot=icon]]:text-white',
    'forced-colors:[&_*[data-slot=icon]]:text-[CanvasText] forced-colors:group-data-focus/option:[&_*[data-slot=icon]]:text-[Canvas]',
    '*:data-[slot=avatar]:-mx-0.5 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:size-5'
  );

  return (
    <Headless.ComboboxOption
      {...props}
      className={clsx(
        'group/option grid w-full cursor-default grid-cols-[1fr_--spacing(5)] items-baseline gap-x-2 rounded-2xl py-2.5 pr-2 pl-3.5 sm:grid-cols-[1fr_--spacing(4)] sm:py-1.5 sm:pr-2 sm:pl-3',
        'text-base/6 sm:text-sm/6',
        'outline-hidden',
        'data-focus:bg-[var(--in-ring)] data-focus:text-white',
        'hover:bg-[var(--in-border-hover)]',
        'data-selected:bg-[var(--in-ring)] data-selected:text-white',
        'data-disabled:opacity-50',
        'text-[var(--in-fg)]',
        className
      )}
    >
      <span className={clsx(sharedClasses)}>{children}</span>
      <svg
        className="relative col-start-2 hidden size-5 self-center stroke-current group-data-selected/option:inline sm:size-4"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 8.5l3 3L12 4"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Headless.ComboboxOption>
  );
}

export function ComboboxLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0'
      )}
    />
  );
}

export function ComboboxDescription({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'flex flex-1 overflow-hidden text-[var(--in-ph)] group-data-focus/option:text-white before:w-2 before:min-w-0 before:shrink'
      )}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  );
}
